import React, { useState } from "react";
import { Alert, Button, FormGroup, Input, Label } from "reactstrap";

const EMAIL = /^[^\s,@]+@[^\s,@]+\.[^\s,@]+$/;
const PREVIEW = 3;
const MAX_MEMBERS = 1000;

export const parseEmails = (text) => {
  const entries = [...new Set(text.split(/[\s,;]+/).map((e) => e.trim().toLowerCase()).filter(Boolean))];
  return { valid: entries.filter((e) => EMAIL.test(e)), invalid: entries.filter((e) => !EMAIL.test(e)) };
};

const dateLabel = (value) =>
  new Date(`${value}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

// Saved cohorts (pasted email lists) and the events log (dated notes on the weekly
// chart). Both are simple records a developer adds by hand; nothing is tracked
// automatically.
export default function FeedbackCohorts({ cohorts, annotations, cohortStats = {}, onSaveCohort, onDeleteCohort, onSaveAnnotation, onDeleteAnnotation }) {
  const [name, setName] = useState("");
  const [emails, setEmails] = useState("");
  const [cohortError, setCohortError] = useState("");
  const [savingCohort, setSavingCohort] = useState(false);
  const [noteDate, setNoteDate] = useState("");
  const [noteLabel, setNoteLabel] = useState("");
  const [noteCohort, setNoteCohort] = useState("");
  const [noteError, setNoteError] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  const parsed = parseEmails(emails);
  const tooMany = parsed.valid.length > MAX_MEMBERS;
  const canSaveCohort = name.trim() && parsed.valid.length && !parsed.invalid.length && !tooMany && !savingCohort;
  const canSaveNote = noteDate && noteLabel.trim() && !savingNote;

  const saveCohort = (event) => {
    event.preventDefault();
    setSavingCohort(true);
    setCohortError("");
    Promise.resolve(onSaveCohort(name.trim(), parsed.valid)).then(() => {
      setName("");
      setEmails("");
    }).catch((error) => setCohortError(error?.message || "Unable to save the cohort."))
      .finally(() => setSavingCohort(false));
  };

  const saveNote = (event) => {
    event.preventDefault();
    setSavingNote(true);
    setNoteError("");
    Promise.resolve(onSaveAnnotation(noteDate, noteLabel.trim(), noteCohort || null)).then(() => {
      setNoteDate("");
      setNoteLabel("");
      setNoteCohort("");
    }).catch((error) => setNoteError(error?.message || "Unable to save the note."))
      .finally(() => setSavingNote(false));
  };

  const sorted = [...annotations].sort((a, b) => (a.date < b.date ? -1 : 1));

  return (
    <div className="fb-two-col">
      <div className="note-intel-panel fb-panel">
        <div className="note-intel-panel-header">
          <div>
            <h3>Cohorts</h3>
            <p>Saved groups of clinicians, like a beta group. Paste their emails to make one, then compare cohorts on the chart above.</p>
          </div>
        </div>
        {!cohorts.length && <p className="fb-muted">No cohorts yet.</p>}
        <div className="fb-cohort-list">
          {cohorts.map((cohort) => (
            <article className="fb-cohort" key={cohort.cohortId}>
              <div className="fb-cohort-head">
                <strong>{cohort.name}</strong>
                <span className="fb-muted">{cohort.userIds.length.toLocaleString()} {cohort.userIds.length === 1 ? "clinician" : "clinicians"}</span>
              </div>
              <p className="fb-cohort-rate">
                {cohortStats[cohort.cohortId]?.rated
                  ? <>Thumbs-down rate <strong>{cohortStats[cohort.cohortId].downRate}%</strong>
                      <span className="fb-muted"> · {cohortStats[cohort.cohortId].rated.toLocaleString()} rated in these filters</span></>
                  : <span className="fb-muted">No ratings in these filters</span>}
              </p>
              <p className="fb-cohort-members" title={cohort.userIds.length <= 50 ? cohort.userIds.join(", ") : undefined}>
                {cohort.userIds.slice(0, PREVIEW).join(", ")}
                {cohort.userIds.length > PREVIEW && ` +${cohort.userIds.length - PREVIEW} more`}
              </p>
              <Button size="sm" color="link" className="fb-remove"
                onClick={() => window.confirm(`Remove the cohort "${cohort.name}"? Notes tied to it stay, as notes for everyone.`) && onDeleteCohort(cohort.cohortId)}>
                Remove
              </Button>
            </article>
          ))}
        </div>
        <form className="fb-form" onSubmit={saveCohort}>
          <h4>New cohort</h4>
          <FormGroup>
            <Label for="fb-cohort-name">Cohort name</Label>
            <Input id="fb-cohort-name" value={name} maxLength={80} placeholder="e.g. Learn++ beta group"
              onChange={(event) => setName(event.target.value)} />
          </FormGroup>
          <FormGroup>
            <Label for="fb-cohort-emails">Emails</Label>
            <Input id="fb-cohort-emails" type="textarea" rows={3} value={emails}
              placeholder="Paste emails, separated by commas, spaces or new lines"
              onChange={(event) => setEmails(event.target.value)} />
            <small className="fb-muted">
              {parsed.valid.length} {parsed.valid.length === 1 ? "email" : "emails"}
              {parsed.invalid.length ? ` · not valid: ${parsed.invalid.slice(0, 3).join(", ")}${parsed.invalid.length > 3 ? "…" : ""}` : ""}
              {tooMany ? ` · a cohort can have up to ${MAX_MEMBERS.toLocaleString()}` : ""}
            </small>
          </FormGroup>
          {cohortError && <Alert color="danger">{cohortError}</Alert>}
          <Button size="sm" color="primary" type="submit" disabled={!canSaveCohort}>Save cohort</Button>
        </form>
      </div>

      <div className="note-intel-panel fb-panel">
        <div className="note-intel-panel-header">
          <div>
            <h3>Events log</h3>
            <p>Note a change, like “enabled Learn++ for this cohort” or “updated the assessment prompt”. Each one is a numbered line on the chart.</p>
          </div>
        </div>
        <form className="fb-form fb-note-form" onSubmit={saveNote}>
          <FormGroup>
            <Label for="fb-note-date">Date</Label>
            <Input id="fb-note-date" type="date" value={noteDate} onChange={(event) => setNoteDate(event.target.value)} />
          </FormGroup>
          <FormGroup>
            <Label for="fb-note-cohort">Cohort (optional)</Label>
            <Input id="fb-note-cohort" type="select" value={noteCohort} onChange={(event) => setNoteCohort(event.target.value)}>
              <option value="">Everyone</option>
              {cohorts.map((cohort) => <option key={cohort.cohortId} value={cohort.cohortId}>{cohort.name}</option>)}
            </Input>
          </FormGroup>
          <FormGroup className="fb-note-label">
            <Label for="fb-note-label">What changed</Label>
            <Input id="fb-note-label" value={noteLabel} maxLength={200} placeholder="e.g. Updated the assessment prompt"
              onChange={(event) => setNoteLabel(event.target.value)} />
          </FormGroup>
          <Button size="sm" color="primary" type="submit" disabled={!canSaveNote}>Add to chart</Button>
        </form>
        {noteError && <Alert color="danger">{noteError}</Alert>}
        {!sorted.length && <p className="fb-muted">No notes yet.</p>}
        <ul className="fb-note-list">
          {sorted.map((note) => (
            <li key={note.annotationId}>
              <div>
                <strong>{note.label}</strong>
                <span className="fb-muted">
                  {dateLabel(note.date)} · {cohorts.find((c) => c.cohortId === note.cohortId)?.name || "Everyone"}
                </span>
              </div>
              <Button size="sm" color="link" className="fb-remove" onClick={() => onDeleteAnnotation(note.annotationId)}>Remove</Button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
